import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DollarSign, Loader2 } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useBulkEditSubscriptionPrice, Subscription } from '@/lib/api/subscriptions'
import { moneyStringOrNumberToMinorUnits, getCurrencyInfo } from '@/lib/money'

interface UpdatePriceBulkActionModalProps {
	subscriptionIds: string[]
	subscriptions: Subscription[]
	size?: 'sm' | 'md'
}

const getCommonCurrency = (
	subscriptions: Subscription[],
	selectedIds: string[],
): string | null => {
	const selected = subscriptions.filter((s) => selectedIds.includes(s.id))
	if (selected.length === 0) return null

	const currencies = new Set(selected.map((s) => s.currency))
	return currencies.size === 1 ? (selected[0]?.currency ?? null) : null
}

const getCurrencySymbol = (currencyCode: string): string =>
	getCurrencyInfo(currencyCode)?.symbol ?? currencyCode

export const UpdatePriceBulkActionModal = ({
	subscriptionIds,
	subscriptions,
	size = 'sm',
}: UpdatePriceBulkActionModalProps) => {
	const { t } = useTranslation('subscriptions')
	const { mutate: updatePriceMutation, isPending } = useBulkEditSubscriptionPrice()
	const [isOpen, setIsOpen] = useState(false)
	const [priceInput, setPriceInput] = useState('')

	const commonCurrency = getCommonCurrency(subscriptions, subscriptionIds)
	const currencySymbol = commonCurrency ? getCurrencySymbol(commonCurrency) : null
	const hasMixedCurrencies = !commonCurrency && subscriptionIds.length > 0
	const isValid = priceInput !== '' && Number(priceInput) > 0 && !hasMixedCurrencies

	const handleOpen = useCallback(() => {
		setPriceInput('')
		setIsOpen(true)
	}, [])

	const handleSubmit = useCallback(() => {
		if (!isValid || !commonCurrency) return

		const amountInMinorUnits = moneyStringOrNumberToMinorUnits(priceInput, commonCurrency)
		updatePriceMutation(
			{ subscriptionIds, amount: amountInMinorUnits },
			{ onSettled: () => setIsOpen(false) },
		)
	}, [isValid, commonCurrency, priceInput, subscriptionIds, updatePriceMutation])

	return (
		<>
			{size === 'sm' && (
				<DataTableActionBarAction
					tooltip={t('bulkActions.updatePrice.tooltip')}
					isPending={isPending}
					onClick={handleOpen}
				>
					<DollarSign className="mr-2 h-4 w-4" />
					{t('bulkActions.updatePrice.button')}
				</DataTableActionBarAction>
			)}
			{size === 'md' && (
				<Button variant="outline" size="sm" onClick={handleOpen}>
					<DollarSign className="mr-2 h-4 w-4" />
					{t('bulkActions.updatePrice.button')}
				</Button>
			)}
			<Dialog open={isOpen} onOpenChange={(open) => !open && setIsOpen(false)}>
				<DialogContent className="sm:max-w-[420px]">
					<DialogHeader>
						<DialogTitle>{t('bulkActions.updatePrice.title')}</DialogTitle>
						<DialogDescription>
							{t('bulkActions.updatePrice.confirmMessage', { count: subscriptionIds.length })}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						{hasMixedCurrencies && (
							<p className="text-destructive text-sm">
								{t('bulkActions.updatePrice.mixedCurrenciesError')}
							</p>
						)}
						<div className="space-y-2">
							<Label htmlFor="price-input">
								{t('bulkActions.updatePrice.newPrice')}
								{commonCurrency && (
									<span className="text-muted-foreground ml-1">({commonCurrency})</span>
								)}
							</Label>
							<div className="relative">
								{currencySymbol && (
									<span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 text-sm">
										{currencySymbol}
									</span>
								)}
								<Input
									id="price-input"
									type="number"
									min="0"
									step="0.01"
									placeholder="0.00"
									value={priceInput}
									onChange={(e) => setPriceInput(e.target.value)}
									className={currencySymbol ? 'pl-7' : ''}
									disabled={hasMixedCurrencies}
								/>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button onClick={() => setIsOpen(false)} variant="outline">
							{t('bulkActions.updatePrice.cancel')}
						</Button>
						<Button onClick={handleSubmit} disabled={isPending || !isValid}>
							{isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
							{t('bulkActions.updatePrice.update')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
